import React, { useState } from 'react';
import { Box, Typography, Paper, IconButton, Collapse } from '@mui/material';
import { ExpandMore, ExpandLess } from '@mui/icons-material';
import { FrequencySpectrum } from '../types';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

interface FrequencyResponseChartProps {
  target: FrequencySpectrum;
  reference: FrequencySpectrum;
  mastered: FrequencySpectrum;
  masteredFilename?: string;
}

const FrequencyResponseChart: React.FC<FrequencyResponseChartProps> = ({
  target,
  reference,
  mastered,
  masteredFilename,
}) => {
  const [expanded, setExpanded] = useState(true);

  // Prepare data for chart
  const chartData = target.frequencies.map((freq, index) => ({
    frequency: freq,
    target: target.magnitudes[index],
    reference: reference.magnitudes[index],
    mastered: mastered.magnitudes[index],
  }));

  // Format frequency for display (20 Hz, 1 kHz, 10 kHz, etc.)
  const formatFrequency = (freq: number) => {
    if (freq >= 1000) {
      return `${(freq / 1000).toFixed(freq >= 10000 ? 0 : 1)}k`;
    }
    return `${freq.toFixed(0)}`;
  };

  // Custom tooltip
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const freq = payload[0].payload.frequency;
      return (
        <Paper sx={{ p: 1, bgcolor: 'background.paper' }}>
          <Typography variant="caption" display="block" fontWeight="bold">
            {formatFrequency(freq)} Hz
          </Typography>
          {payload.map((entry: any) => (
            <Typography key={entry.name} variant="caption" display="block" sx={{ color: entry.color }}>
              {entry.name}: {entry.value.toFixed(1)} dB
            </Typography>
          ))}
        </Paper>
      );
    }
    return null;
  };


  return (
    <Paper elevation={2} sx={{ p: 2.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="h6">
          Frequency Response Comparison
        </Typography>
        <IconButton
          size="small"
          onClick={() => setExpanded(!expanded)}
          sx={{ display: { xs: 'inline-flex', md: 'none' } }}
        >
          {expanded ? <ExpandLess /> : <ExpandMore />}
        </IconButton>
      </Box>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
        Shows the average frequency spectrum of the entire track (single FFT analysis).
        The chart reveals what EQ changes were applied across the full frequency range.
        Green line shows the mastered output after Matchering's spectral matching.
        {masteredFilename && (
          <>
            <br />
            <strong>Master:</strong> {masteredFilename}
          </>
        )}
      </Typography>

      <Collapse in={expanded}>
        <Box sx={{
          width: '100%',
          height: { xs: 300, sm: 350, md: 400 },
          minHeight: 250
        }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={chartData}
              margin={{ top: 5, right: 40, left: 0, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
              <XAxis
                dataKey="frequency"
                scale="log"
                domain={[20, 20000]}
                tickFormatter={formatFrequency}
                label={{ value: 'Frequency (Hz)', position: 'insideBottom', offset: -5 }}
              />
              <YAxis
                label={{ value: 'Magnitude (dB)', angle: -90, position: 'insideLeft' }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Line
                type="monotone"
                dataKey="target"
                stroke="#2196f3"
                strokeWidth={2}
                dot={false}
                name="Original Target"
              />
              <Line
                type="monotone"
                dataKey="reference"
                stroke="#9c27b0"
                strokeWidth={2}
                dot={false}
                name="Reference"
              />
              <Line
                type="monotone"
                dataKey="mastered"
                stroke="#4caf50"
                strokeWidth={3}
                dot={false}
                name="Mastered Output"
              />
            </LineChart>
          </ResponsiveContainer>
        </Box>

        <Box sx={{ mt: 2, display: 'flex', gap: { xs: 1, sm: 2 }, flexWrap: 'wrap', justifyContent: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 16, height: 3, bgcolor: '#2196f3', flexShrink: 0 }} />
            <Typography variant="caption" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>Original Target</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 16, height: 3, bgcolor: '#9c27b0', flexShrink: 0 }} />
            <Typography variant="caption" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>Reference</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 16, height: 3, bgcolor: '#4caf50', flexShrink: 0 }} />
            <Typography variant="caption" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>Mastered Output</Typography>
          </Box>
        </Box>
      </Collapse>
    </Paper>
  );
};

export default FrequencyResponseChart;
